import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Layout, Menu, Button, Typography, Space, Drawer, Grid, Spin } from 'antd';
import {
  DashboardOutlined,
  FileTextOutlined,
  ShoppingCartOutlined,
  SettingOutlined,
  LogoutOutlined,
  UserOutlined,
  MenuOutlined,
  DatabaseOutlined,
  InboxOutlined,
  AuditOutlined,
  ShopOutlined,
  LeftOutlined,
  RightOutlined,
  RollbackOutlined,
  SkinOutlined,
  PieChartOutlined,
} from '@ant-design/icons';
import ResourceGuard from './components/ResourceGuard';
import ChatDrawer from './components/ChatDrawer';
import { AuthProvider, ProtectedRoute, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import NotificationBell from './components/NotificationBell';
import GlobalSearch from './components/GlobalSearch'; // global search 2026-09-06
import Login from './pages/Login';
import Dashboard from './pages/purchasing/Dashboard';
import MRList from './pages/purchasing/MRList';
import MRCreate from './pages/purchasing/MRCreate';
import MRView from './pages/purchasing/MRView';
import POList from './pages/purchasing/POList';
import POCreate from './pages/purchasing/POCreate';
import POView from './pages/purchasing/POView';
import Admin from './pages/admin/Admin';
import CatalogList from './pages/purchasing/CatalogList';
import GRNList from './pages/purchasing/GRNList';
import GRNView from './pages/purchasing/GRNView';
import AuditTrail from './pages/purchasing/AuditTrail';
const VendorList = React.lazy(() => import('./pages/purchasing/VendorList'));
const VendorDetail = React.lazy(() => import('./pages/purchasing/VendorDetail'));
const VendorPendingQueue = React.lazy(() => import('./pages/purchasing/VendorPendingQueue'));
const ItemCard = React.lazy(() => import('./pages/purchasing/ItemCard'));
const ComposerSync = React.lazy(() => import('./pages/purchasing/ComposerSync'));
const ReturnList = React.lazy(() => import('./pages/purchasing/ReturnList'));
const Cancellations = React.lazy(() => import('./pages/purchasing/Cancellations')); // queue full page (2026-09-16)
const SamplesList = React.lazy(() => import('./pages/purchasing/samples/SamplesList'));
const SampleDetail = React.lazy(() => import('./pages/purchasing/samples/SampleDetail'));
const CostAllocation = React.lazy(() => import('./pages/purchasing/CostAllocation')); // Cost allocation 2026-09-05

const { Header, Sider, Content } = Layout;
const { Text } = Typography;
const { useBreakpoint } = Grid;

const SIDER_WIDTH = 200;
const SIDER_COLLAPSED_WIDTH = 80;

const AppLayout: React.FC = () => {
  const { user, logout, isAdmin, canAccess } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const isMobile = !screens.lg;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const getSelectedKey = () => {
    const path = location.pathname;
    if (path === '/' || path === '/dashboard') return 'dashboard';
    if (path.startsWith('/mrs')) return 'mrs';
    if (path.startsWith('/pos')) return 'pos';
    if (path.startsWith('/grns')) return 'grns';
    if (path.startsWith('/returns')) return 'returns';
    if (path.startsWith('/catalog')) return 'catalog';
    if (path.startsWith('/samples') || path.startsWith('/client-samples')) return 'samples';
    if (path.startsWith('/cost-allocation')) return 'cost-allocation';
    if (path.startsWith('/audit')) return 'audit';
    if (path.startsWith('/admin')) return 'admin';
    return 'dashboard';
  };

  const menuItems: any[] = [];

  // ── Purchasing Module ──
  const purchasingChildren: any[] = [];
  if (canAccess('purchasing.dashboard')) {
    purchasingChildren.push({ key: 'dashboard', icon: <DashboardOutlined />, label: 'Dashboard', onClick: () => navigate('/') });
  }
  if (canAccess('purchasing.mrs')) {
    purchasingChildren.push({ key: 'mrs', icon: <FileTextOutlined />, label: 'Material Requests', onClick: () => navigate('/mrs') });
  }
  if (canAccess('purchasing.pos')) {
    purchasingChildren.push({ key: 'pos', icon: <ShoppingCartOutlined />, label: 'Purchase Orders', onClick: () => navigate('/pos') });
  }
  if (canAccess('purchasing.grns')) {
    purchasingChildren.push({ key: 'grns', icon: <InboxOutlined />, label: 'Goods Receiving', onClick: () => navigate('/grns') });
  }
  if (canAccess('purchasing.returns')) {
    purchasingChildren.push({ key: 'returns', icon: <RollbackOutlined />, label: 'Purchase Returns', onClick: () => navigate('/returns') });
  }
  if (canAccess('purchasing.vendors')) {
    purchasingChildren.push({ key: 'vendors', icon: <ShopOutlined />, label: 'Vendors', onClick: () => navigate('/vendors') });
  }
  if (canAccess('purchasing.catalog')) {
    purchasingChildren.push({ key: 'catalog', icon: <DatabaseOutlined />, label: 'Parts Catalog', onClick: () => navigate('/catalog') });
  }
  if (canAccess('purchasing.client_samples')) {
    purchasingChildren.push({ key: 'samples', icon: <SkinOutlined />, label: 'Samples', onClick: () => navigate('/samples') });
  }
  if (canAccess('purchasing.cost_centers')) {
    purchasingChildren.push({ key: 'cost-allocation', icon: <PieChartOutlined />, label: 'Cost Allocation', onClick: () => navigate('/cost-allocation') });
  }
  if (canAccess('purchasing.audit')) {
    purchasingChildren.push({ key: 'audit', icon: <AuditOutlined />, label: 'Audit Trail', onClick: () => navigate('/audit') });
  }
  if (purchasingChildren.length > 0) {
    menuItems.push({ key: 'purchasing-group', label: 'PURCHASING', type: 'group', children: purchasingChildren });
  }

  // ── System Module ──
  if (isAdmin || canAccess('admin') || canAccess('admin.vendors') || canAccess('admin.categories') || canAccess('admin.units') || canAccess('admin.users') || canAccess('admin.data') || canAccess('admin.permissions')) {
    menuItems.push({ type: 'divider', key: 'admin-divider' });
    menuItems.push({
      key: 'admin-group',
      label: 'SYSTEM',
      type: 'group',
      children: [
        { key: 'admin', icon: <SettingOutlined />, label: 'Admin', onClick: () => navigate('/admin') },
      ],
    });
  }

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const siderContent = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div
        style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <img
          src="/logo.PNG"
          alt="Shahira Lasheen"
          style={{ width: collapsed && !isMobile ? 40 : 130, height: 'auto', filter: 'invert(1)', transition: 'width 0.2s' }}
        />
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[getSelectedKey()]}
          items={menuItems}
          style={{ background: '#000', borderRight: 'none' }}
        />
      </div>
      {/* Collapse toggle — desktop only */}
      {!isMobile && (
        <div
          onClick={() => setCollapsed(!collapsed)}
          style={{
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-end',
            padding: collapsed ? 0 : '0 16px',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            cursor: 'pointer',
            color: 'rgba(255,255,255,0.45)',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.45)'; e.currentTarget.style.background = 'transparent'; }}
        >
          {collapsed ? <RightOutlined style={{ fontSize: 14 }} /> : (
            <Space size={8}>
              <Text style={{ color: 'inherit', fontSize: 12 }}>Collapse</Text>
              <LeftOutlined style={{ fontSize: 12 }} />
            </Space>
          )}
        </div>
      )}
    </div>
  );

  const currentMarginLeft = isMobile ? 0 : (collapsed ? SIDER_COLLAPSED_WIDTH : SIDER_WIDTH);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Desktop: fixed sidebar */}
      {!isMobile && (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          breakpoint="lg"
          collapsedWidth={SIDER_COLLAPSED_WIDTH}
          width={SIDER_WIDTH}
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            background: '#000',
            zIndex: 100,
          }}
          trigger={null}
        >
          {siderContent}
        </Sider>
      )}

      {/* Mobile: drawer sidebar */}
      {isMobile && (
        <Drawer
          placement="left"
          onClose={() => setDrawerOpen(false)}
          open={drawerOpen}
          width={250}
          bodyStyle={{ padding: 0, background: '#000' }}
          headerStyle={{ display: 'none' }}
        >
          {siderContent}
        </Drawer>
      )}

      <Layout style={{ marginLeft: currentMarginLeft, transition: 'margin-left 0.2s' }}>
        <Header
          style={{
            padding: isMobile ? '0 12px' : '0 24px',
            background: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #f0f0f0',
            position: 'sticky',
            top: 0,
            zIndex: 99,
          }}
        >
          {isMobile ? (
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setDrawerOpen(true)}
              style={{ fontSize: 18 }}
            />
          ) : (
            <div />
          )}
          <Space size={isMobile ? 'small' : 'middle'}>
            {canAccess('purchasing') && <GlobalSearch compact={isMobile} />}
            <NotificationBell />
            {!isMobile && (
              <Space>
                <UserOutlined />
                <Text strong>{user?.displayName}</Text>
                <Text type="secondary">({user?.departmentCode} - {(user?.roles?.length ? user.roles : [user?.role]).map(r => r?.replace(/_/g, ' ')).join(', ')})</Text>
              </Space>
            )}
            {isMobile && (
              <Text strong style={{ fontSize: 12 }}>{user?.displayName}</Text>
            )}
            <Button
              type="text"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
            >
              {!isMobile && 'Logout'}
            </Button>
          </Space>
        </Header>
        <Content style={{
          margin: isMobile ? 8 : 24,
          padding: isMobile ? 12 : 24,
          background: '#fff',
          borderRadius: 8,
          minHeight: 'calc(100vh - 112px)',
          overflow: 'auto',
        }}>
          <Routes>
            <Route path="/" element={<ResourceGuard resource="purchasing.dashboard"><Dashboard /></ResourceGuard>} />
            <Route path="/dashboard" element={<ResourceGuard resource="purchasing.dashboard"><Dashboard /></ResourceGuard>} />
            <Route path="/mrs" element={<ResourceGuard resource="purchasing.mrs"><MRList /></ResourceGuard>} />
            <Route path="/mrs/new" element={<ResourceGuard resource="purchasing.mrs" minLevel="create"><MRCreate /></ResourceGuard>} />
            <Route path="/mrs/:mrId" element={<ResourceGuard resource="purchasing.mrs"><MRView /></ResourceGuard>} />
            <Route path="/mrs/:mrId/edit" element={<ResourceGuard resource="purchasing.mrs" minLevel="create"><MRCreate /></ResourceGuard>} />
            <Route path="/pos" element={<ResourceGuard resource="purchasing.pos"><POList /></ResourceGuard>} />
            <Route path="/pos/new" element={<ResourceGuard resource="purchasing.pos" minLevel="create"><POCreate /></ResourceGuard>} />
            <Route path="/pos/:poId" element={<ResourceGuard resource="purchasing.pos"><POView /></ResourceGuard>} />
            <Route path="/grns" element={<ResourceGuard resource="purchasing.grns"><GRNList /></ResourceGuard>} />
            <Route path="/grns/:grnId" element={<ResourceGuard resource="purchasing.grns"><GRNView /></ResourceGuard>} />
            <Route path="/returns" element={<ResourceGuard resource="purchasing.returns"><Suspense fallback={<Spin size="large" />}><ReturnList /></Suspense></ResourceGuard>} />
            <Route path="/cancellations" element={<ResourceGuard resource="purchasing.pos"><Suspense fallback={<Spin size="large" />}><Cancellations /></Suspense></ResourceGuard>} />
            <Route path="/vendors" element={<ResourceGuard resource="purchasing.vendors"><Suspense fallback={<Spin size="large" />}><VendorList /></Suspense></ResourceGuard>} />
            <Route path="/vendors/pending" element={<ResourceGuard resource="purchasing.vendors" minLevel="full"><Suspense fallback={<Spin size="large" />}><VendorPendingQueue /></Suspense></ResourceGuard>} />
            <Route path="/vendors/:id" element={<ResourceGuard resource="purchasing.vendors"><Suspense fallback={<Spin size="large" />}><VendorDetail /></Suspense></ResourceGuard>} />
            <Route path="/catalog" element={<ResourceGuard resource="purchasing.catalog"><CatalogList /></ResourceGuard>} />
            <Route path="/catalog/composer-sync" element={<ResourceGuard resource="purchasing.catalog" minLevel="full"><Suspense fallback={<Spin size="large" />}><ComposerSync /></Suspense></ResourceGuard>} />
            <Route path="/catalog/composer-sync/:batchId" element={<ResourceGuard resource="purchasing.catalog" minLevel="full"><Suspense fallback={<Spin size="large" />}><ComposerSync /></Suspense></ResourceGuard>} />
            <Route path="/catalog/:id" element={<ResourceGuard resource="purchasing.catalog"><Suspense fallback={<Spin size="large" />}><ItemCard /></Suspense></ResourceGuard>} />
            <Route path="/samples" element={<ResourceGuard resource="purchasing.client_samples"><Suspense fallback={<Spin size="large" />}><SamplesList /></Suspense></ResourceGuard>} />
            <Route path="/samples/:code" element={<ResourceGuard resource="purchasing.client_samples"><Suspense fallback={<Spin size="large" />}><SampleDetail /></Suspense></ResourceGuard>} />
            <Route path="/client-samples" element={<Navigate to="/samples" replace />} />
            <Route path="/cost-allocation" element={<ResourceGuard resource="purchasing.cost_centers"><Suspense fallback={<Spin size="large" />}><CostAllocation /></Suspense></ResourceGuard>} />
            <Route path="/audit" element={<ResourceGuard resource="purchasing.audit"><AuditTrail /></ResourceGuard>} />
            <Route path="/admin" element={<Admin />} />
            <Route path="*" element={<Navigate to="/" replace /> } />
          </Routes>
        </Content>
      </Layout>
      <ChatDrawer />
    </Layout>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <NotificationProvider>
                  <AppLayout />
                </NotificationProvider>
              </ProtectedRoute>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
